import type { CSSProperties, MouseEvent, ReactNode } from 'react';

export type ChipVariant =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'ai';

interface ChipProps {
  variant?: ChipVariant;
  /** Pílula totalmente arredondada (radius 999px) */
  round?: boolean;
  /** Mostra bolinha colorida 6x6px no início */
  dot?: boolean;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
}

const VARIANT_CLASSES: Record<ChipVariant, string> = {
  neutral: 'bg-[var(--gray-100)] text-[var(--gray-700)]',
  brand:   'bg-[var(--brand-50)] text-[var(--brand-700)] border-[var(--brand-100)]',
  success: 'bg-[var(--success-100)] text-[var(--success-700)]',
  warning: 'bg-[var(--warning-100)] text-[var(--warning-700)]',
  error:   'bg-[var(--error-100)] text-[var(--error-700)]',
  info:    'bg-[var(--info-100)] text-[var(--info-700)]',
  ai:      'bg-[var(--ai-100)] text-[var(--ai-600)] border-[var(--ai-200)]',
};

const DOT_COLORS: Record<ChipVariant, string> = {
  neutral: 'bg-[var(--gray-400)]',
  brand:   'bg-[var(--brand-600)]',
  success: 'bg-[var(--success-500)]',
  warning: 'bg-[var(--warning-500)]',
  error:   'bg-[var(--error-500)]',
  info:    'bg-[var(--info-500)]',
  ai:      'bg-[var(--ai-500)]',
};

/**
 * Badge/Chip atômico com 7 variantes semânticas.
 *
 * @example
 * <Chip variant="success" dot>Aprovado</Chip>
 * <Chip variant="ai" round>IA gerou</Chip>
 */
export default function Chip({
  variant = 'neutral',
  round = false,
  dot = false,
  children,
  className,
  style,
  onClick,
}: ChipProps) {
  const baseClasses =
    'inline-flex items-center gap-1 px-2 py-0.5 text-[11.5px] font-medium leading-[1.5] border border-transparent whitespace-nowrap';
  const radiusClass = round ? 'rounded-full px-[9px] py-[1px]' : 'rounded';
  const variantClass = VARIANT_CLASSES[variant];
  const dotClass = DOT_COLORS[variant];
  const clickableClass = onClick ? 'cursor-pointer' : '';

  return (
    <span
      className={`${baseClasses} ${radiusClass} ${variantClass} ${clickableClass} ${className ?? ''}`}
      style={style}
      onClick={onClick}
    >
      {dot && <span className={`size-1.5 shrink-0 rounded-full ${dotClass}`} aria-hidden="true" />}
      {children}
    </span>
  );
}
